<template>
  <Modal @close="$emit('close')">
    <template #header>
      <div class="w-full text-center">
        <h1 class="text-2xl">
          {{ $props.trade ? $t('HeadContract.BillOfQuantities.TradeSaveModal.update') : $t('HeadContract.BillOfQuantities.TradeSaveModal.create') }}
          <slot name="title">
            {{ $t('HeadContract.BillOfQuantities.TradeSaveModal.title') }}
          </slot>
        </h1>
      </div>
    </template>

    <template #body>
      <div class="h-full w-full overflow-y-auto">
        <form
          action=""
          class="flex flex-col space-y-4"
          @submit.prevent="saveTrade"
        >
          <label>
            {{ $t('HeadContract.BillOfQuantities.TradeSaveModal.label.name') }}
            <AppInput
              v-model="tradeForm.form.name"
              name="name"
              type="text"
              class="form-input"
              :error="tradeForm.getErrors('name')"
              data-test="name-input"
            />
          </label>
        </form>
      </div>
    </template>

    <template #footer>
      <div class="w-full flex justify-end space-x-4">
        <button
          type="button"
          class="btn btn--secondary-blue"
          @click="$emit('close'); tradeForm.resetData()"
        >
          {{ $t('HeadContract.BillOfQuantities.TradeSaveModal.button.cancel') }}
        </button>

        <button
          type="button"
          class="btn btn--primary-blue"
          :disabled="createTradeAction.is(States.LOADING)"
          data-test="save-trade-item"
          @click="saveTrade"
        >
          {{ $props.trade ? $t('HeadContract.BillOfQuantities.TradeSaveModal.update') : $t('HeadContract.BillOfQuantities.TradeSaveModal.create') }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import Trade from '@/models/Trade';
import { useTradesStore } from '@/store/trades';
import Swal from 'sweetalert2';
import { watch } from 'vue';
import { toast } from 'vue3-toastify';

const props = defineProps<{
  projectId: Id;
  trade?: Trade;
  selectedPosition?: number;
  boqType: string;
}>();

const emits = defineEmits<{
  (e: 'close');
  (e: 'updateSort', data: { trade: Trade; index: number });
}>();

const tradeForm = useFormData({
  name: '',
  sort_order: undefined,
  boq_type: props.boqType,
});

watch(() => props.selectedPosition, (newValue) => {
  tradeForm.form.sort_order = newValue;
}, { immediate: true });

watch(() => props.trade, (newValue) => {
  if(newValue) {
    tradeForm.setData(newValue);
  }
}, { immediate: true });

const tradesStore = useTradesStore();
const createTradeAction = useStoreApiAction(tradesStore.createTrade);
const updateTradeAction = useStoreApiAction(tradesStore.updateTrade);

const saveTrade = () => {
  if(props.trade) {
    updateTradeAction.request(props.trade.id, tradeForm.form).then(() => {
      toast.success(props.boqType === 'indirect' ? 'Indirect cost updated.' : 'Trade updated.');
      emits('close');
    }).catch((error) => {
      tradeForm.setErrors(error.data);
    });
  } else {
    createTradeAction.request(props.projectId, tradeForm.form).then((data) => {
      // We need to refresh the listing sort order, this is the easiest way
      emits('updateSort', { trade: data, index: data.sort_order });

      toast.success(props.boqType === 'indirect' ? 'Indirect cost created.' : 'Trade created.');
      emits('close');
    }).catch((error) => {
      tradeForm.setErrors(error.data);
    });
  }
};
</script>

<style scoped></style>
